// Single place that talks to the Intercom messenger.
//
// Why this exists: `@intercom/messenger-js-sdk`'s default export NEVER calls
// `boot`. It only assigns `window.intercomSettings` and then — if the messenger
// already exists on the page — fires `Intercom('update', settings)`. That is the
// wrong verb when the visitor has just logged in. Intercom's documented login
// flow is `shutdown()` then `boot(userProps)`; an `update` on a session that was
// booted anonymously binds the conversation to the `user_id` but leaves the
// contact's name/email empty. Intercom's inbox then falls back to displaying the
// raw `user_id`, which is why support was staring at "41" instead of a person.
//
// So: every identify goes through here, and here decides update-vs-reboot.

import Intercom from '@intercom/messenger-js-sdk';

const APP_ID = process.env.NEXT_PUBLIC_INTERCOM_API_ID;

// Tracks who the live messenger is currently booted as: `null` = anonymous or
// not booted. Module scope is the right lifetime — it must survive Layout1
// unmount/remount during client-side navigation, which is exactly when a
// marketing page and a dashboard page trade places in the same tab.
let identifiedUserId = null;

// Who wants to hear when the identified messenger comes and goes — the
// sidebar's Chat row (left-sidebar-1/chatRow.js), which is the dashboard's
// only way into the messenger now that its floating launcher is hidden.
const listeners = new Set();
const notify = () => listeners.forEach((fn) => fn());

/** True while the messenger is booted for a signed-in user. */
export function isIntercomIdentified() {
  return identifiedUserId !== null;
}

/** useSyncExternalStore-shaped: returns the unsubscribe. */
export function subscribeIntercomIdentified(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/**
 * The name Intercom will show in the inbox. NEVER returns empty: an Intercom
 * contact with no name and no email is displayed as its bare `user_id`, which
 * is useless to support. `fullName` is routinely blank (Free signups never
 * populate it), so fall through username → email before giving up.
 */
export function intercomDisplayName(u) {
  return (
    u?.fullName?.trim() ||
    u?.username?.trim() ||
    u?.email?.trim() ||
    (u?.id ? `User ${u.id}` : 'Unknown user')
  );
}

/**
 * Boot/refresh the messenger for a known, signed-in user.
 *
 * @param {object} payload Intercom settings WITHOUT `app_id` (added here).
 *   Must include `user_id`; should include `name` and `email`.
 */
export function identifyIntercom(payload) {
  if (!APP_ID || typeof window === 'undefined' || !payload?.user_id) return;

  const settings = { app_id: APP_ID, ...payload };
  const live = typeof window.Intercom === 'function';
  const sameUser =
    identifiedUserId !== null &&
    String(identifiedUserId) === String(payload.user_id);

  if (live && !sameUser) {
    // Messenger is up but is either anonymous (marketing-site boot) or bound to
    // a different person (account switch). `update` will not re-identify it —
    // tear it down and boot clean so the contact gets name + email attached.
    window.Intercom('shutdown');
    window.intercomSettings = settings;
    window.Intercom('boot', settings);
  } else if (live) {
    // Same person, changed attributes (plan, company, launcher visibility).
    window.intercomSettings = settings;
    window.Intercom('update', settings);
  } else {
    // First touch in this tab — SDK injects the widget script, which reads
    // `window.intercomSettings` on load and boots identified straight away.
    Intercom(settings);
  }

  identifiedUserId = payload.user_id;
  notify();
}

/**
 * Boot the messenger for an anonymous marketing-site visitor.
 *
 * No-ops once someone is identified: a logged-in user clicking through to the
 * public site must not be downgraded back to a nameless lead.
 */
export function bootAnonymousIntercom() {
  if (!APP_ID || typeof window === 'undefined') return;
  if (identifiedUserId !== null) return;

  const settings = { app_id: APP_ID };

  if (typeof window.Intercom === 'function') {
    // Messenger script is already on the page — most commonly because it was
    // shut down (consent withdrawal, sign-out) and is being brought back. The
    // SDK default export would only `update` here, which does not re-open a
    // shut-down messenger, so boot explicitly.
    window.intercomSettings = settings;
    window.Intercom('boot', settings);
  } else {
    // First touch in this tab — SDK injects the widget script, which boots
    // anonymously off `window.intercomSettings` on load.
    Intercom(settings);
  }
}

/**
 * Open the messenger on a new conversation, optionally pre-filled.
 *
 * Returns true when the messenger was on the page and took the call, false
 * when it is not loaded (no app id, marketing consent withheld, script
 * blocked) so the caller can fall back to another contact path. Never throws.
 */
export function openIntercomMessenger(prefill = '') {
  if (typeof window === 'undefined' || typeof window.Intercom !== 'function') {
    return false;
  }
  try {
    window.Intercom('showNewMessage', prefill);
    return true;
  } catch {
    return false;
  }
}

/**
 * Open the messenger on its home screen (past conversations + "Send us a
 * message"). Same contract as openIntercomMessenger: false when it is not on
 * the page, never throws.
 */
export function showIntercomMessenger() {
  if (typeof window === 'undefined' || typeof window.Intercom !== 'function') {
    return false;
  }
  try {
    window.Intercom('show');
    return true;
  } catch {
    return false;
  }
}

/**
 * Shut the messenger down (marketing-consent withdrawal, sign-out) and forget
 * who it was booted as, so the next identify does a full re-boot.
 */
export function shutdownIntercom() {
  const wasIdentified = identifiedUserId !== null;
  identifiedUserId = null;
  if (wasIdentified) notify();
  if (typeof window === 'undefined' || typeof window.Intercom !== 'function') {
    return;
  }
  window.Intercom('shutdown');
}
